import type { Money } from "../../domain/types";

export type BuyerFundingFlowInstructions = {
  versionLabel: string;
  publishedAt: string;
  paymentRail: string;
  deadlineAt: string;
  beneficiaryName: string;
  bankName: string;
  routingLast4: string;
  accountLast4: string;
  memoRequired: string;
  /** Escrow / trust account name (Credit to). */
  creditTo?: string;
  furtherCreditTo?: string;
  correspondentBank?: string;
  officeContactNote?: string;
};

export type BuyerFundingFlowPlaidEvidence = {
  mode: "live" | "simulated" | "skipped" | "manual";
  connectedAt: string;
  ownerName?: string;
  institutionName?: string;
  accountLast4?: string;
  routingLast4?: string;
  /** Plaid item id after server exchange (not an access token). */
  itemId?: string;
  identityOwnerNames?: string[];
  ownerMatchHint?: string;
  authAccountCount?: number;
  limitation: string;
};

export type BuyerFundingFlowAddressValidation = {
  validatedAt: string;
  inputAddress: string;
  formattedAddress?: string;
  placeId?: string;
  verdict?: string;
  addressGranularity?: string;
  limitation: string;
};

export type BuyerFundingFlowInstructionAck = {
  method: "passkey" | "checkbox";
  instructionVersion: string;
  instructionPublishedAt: string;
  acknowledgedAt: string;
  passkeyCredentialId?: string;
  limitation: string;
};

export type BuyerFundingFlowDocuSignAck = {
  mode: "live" | "simulated";
  envelopeId: string;
  instructionVersion: string;
  instructionVersionLabel: string;
  instructionHash: string;
  documentHash: string;
  signedAt: string;
  signerName: string;
  signerEmail?: string;
  certificateHash?: string;
  signedDocumentBase64?: string;
  limitation: string;
};

export const BUYER_FUNDING_FLOW_MANUAL_SOURCE_LIMITATION =
  "Buyer-entered source account details for office review only. Veto does not verify this account or approve the payment.";

export type BuyerFundingFlowDeviceVerification = {
  maskedPhone: string;
  verifiedAt: string;
  mode: "simulated" | "live";
  limitation: string;
};

export type BuyerFundingFlowIdentityVerification = {
  verifiedAt: string;
  displayName: string;
  mode: "simulated" | "live";
  personaInquiryId?: string;
  personaStatus?: string;
  limitation: string;
};

export type BuyerFundingFlowPaymentRequest = {
  requestId: string;
  purpose: string;
  requestedAt: string;
  requestedBy: string;
  /** Office may carry separate insurance; Veto never implies coverage. */
  officeCoverageNote?: string;
};

export type BuyerFundingFlowPaymentInitiation = {
  initiatedAt: string;
  rail: string;
  amountCents: number;
  reference?: string;
  limitation: string;
};

export type BuyerFundingFlowSession = {
  fileCode: string;
  officeName: string;
  buyerName: string;
  buyerEmail?: string;
  buyerLastName: string;
  propertyAddress: string;
  amountDue: Money;
  amountDueLookup: string;
  closingDate?: string;
  invitePhoneMasked: string;
  paymentRequest: BuyerFundingFlowPaymentRequest;
  instructions: BuyerFundingFlowInstructions;
  deviceVerification?: BuyerFundingFlowDeviceVerification;
  identityVerification?: BuyerFundingFlowIdentityVerification;
  instructionsDownloadedAt?: string;
  plaid?: BuyerFundingFlowPlaidEvidence;
  addressValidation?: BuyerFundingFlowAddressValidation;
  instructionAck?: BuyerFundingFlowInstructionAck;
  docuSignAck?: BuyerFundingFlowDocuSignAck;
  paymentInitiation?: BuyerFundingFlowPaymentInitiation;
  acknowledgedAt?: string;
  conflictReport?: {
    source: string;
    attackerRoutingLast4: string;
    attackerAccountLast4: string;
    reportedAt: string;
    note: string;
  };
};

export const BUYER_FUNDING_FLOW_BOUNDARY =
  "These are the office’s current funding instructions. Veto does not move money, verify funds, or approve your payment. The office decides.";

export const BUYER_FUNDING_FLOW_PLAID_LIMITATION =
  "Account owner and balance evidence was recorded at the time of connection. This is evidence for the office review, not a guarantee of settlement or final funds availability.";
